/**
 * Optional click/place sounds, generated with WebAudio — no audio assets. Muted by default. [P6-polish]
 */

export type SoundName = 'place' | 'meeple' | 'score' | 'invalid' | 'turn';

export class SoundBoard {
  private ctx: AudioContext | null = null;
  private _muted = true;

  get muted(): boolean {
    return this._muted;
  }

  setMuted(muted: boolean): void {
    this._muted = muted;
  }

  toggle(): boolean {
    this._muted = !this._muted;
    return this._muted;
  }

  private ensureContext(): AudioContext | null {
    if (this._muted) return null;
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      this.ctx = new Ctor();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  play(name: SoundName): void {
    const ctx = this.ensureContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);

    const tone = (freq: number, start: number, dur: number, type: OscillatorType, peak: number) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, now + start);
      g.gain.setValueAtTime(0, now + start);
      g.gain.linearRampToValueAtTime(peak, now + start + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    };

    switch (name) {
      case 'place':
        tone(180, 0, 0.09, 'sine', 0.18);
        tone(120, 0.04, 0.12, 'sine', 0.12);
        break;
      case 'meeple':
        tone(520, 0, 0.07, 'triangle', 0.1);
        break;
      case 'score':
        tone(660, 0, 0.09, 'triangle', 0.12);
        tone(880, 0.08, 0.14, 'triangle', 0.12);
        break;
      case 'invalid':
        tone(140, 0, 0.14, 'sawtooth', 0.08);
        break;
      case 'turn':
        tone(440, 0, 0.06, 'sine', 0.08);
        tone(560, 0.07, 0.08, 'sine', 0.08);
        break;
    }
  }
}
